import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import { FiCheck, FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { toast } from "react-toastify";
import "./PostManagement.css";

const PostManagement = () => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({
    pendingApproval: 0,
    adminApproved: 0,
    accepted: 0,
    complete: 0,
    adminRejected: 0,
    influencerRejected: 0,
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // ← Changed default to "all"
  
  const limit = 10;

  const [rejectModal, setRejectModal] = useState({ show: false, item: null, reason: "" });

  /* ==================== FETCH POSTS ==================== */
  const fetchPosts = async (pg = page, status = statusFilter) => {
    setLoading(true);
    try {
      const params = { page: pg, limit };

      if (status !== "all") {
        params.status = status;
      }

      console.log("📡 Fetching with params:", params); // Debug

      const res = await api.get("/promotion-post/admin/all", { params });
      console.log("✅ Full API Response:", res.data); // Very useful for debugging

      setPosts(res.data.data || []);
      setTotalPages(res.data.pagination?.totalPages || 1);

      // Normalize status keys (backend sometimes uses snake_case)
      const rawStats = res.data.statusSummary || {};
      setStats({
        pendingApproval: rawStats.pendingApproval || rawStats.pending_approval || 0,
        adminApproved: rawStats.adminApproved || rawStats.admin_approved || 0,
        accepted: rawStats.accepted || 0,
        complete: rawStats.complete || 0,
        adminRejected: rawStats.adminRejected || rawStats.admin_rejected || 0,
        influencerRejected: rawStats.influencerRejected || rawStats.influencerRejected || 0,
      });
    } catch (err) {
      console.error("Fetch posts error:", err.response?.data || err);
      toast.error("Failed to fetch posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts(page, statusFilter);
  }, [page, statusFilter]);

  const handleStatusTab = (newStatus) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handleApprove = async (id) => {
    if (!window.confirm("Approve this post?")) return;
    try {
      await api.put(`/promotion-post/admin/${id}/status`, { status: "adminApproved" });
      toast.success("Post approved");
      fetchPosts(); // Refresh
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve");
    }
  };

  const submitReject = async () => {
    if (!rejectModal.reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    try {
      await api.put(`/promotion-post/admin/${rejectModal.item._id}/status`, {
        status: "adminRejected",
        rejectionReason: rejectModal.reason,
      });
      toast.success("Post rejected");
      setRejectModal({ show: false, item: null, reason: "" });
      fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject");
    }
  };

  /* ==================== HELPERS ==================== */
  const fmt = (dateStr) => dateStr 
    ? new Date(dateStr).toLocaleDateString("en-IN", { 
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" 
      }) 
    : "N/A";

  const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "N/A";

  const formatStatus = (s) => {
    const map = {
      pendingApproval: "Pending Approval",
      adminApproved: "Admin Approved",
      accepted: "Accepted",
      complete: "Complete",
      adminRejected: "Admin Rejected",
      influencerRejected: "Influencer Rejected"
    };
    return map[s] || capitalize(s);
  };

  const statCards = [
    { label: "Pending Approval", key: "pendingApproval", color: "#f59e0b" },
    { label: "Admin Approved", key: "adminApproved", color: "#10b981" },
    { label: "Accepted", key: "accepted", color: "#6366f1" },
    { label: "Complete", key: "complete", color: "#3b82f6" },
    { label: "Admin Rejected", key: "adminRejected", color: "#ef4444" },
  ];

  return (
    <div className="pm-page">
      <div className="pm-content">
        <div className="pm-header">
          <h1>Post Management</h1>
          <p className="pm-subtitle">Manage and review influencer promotion posts</p>
        </div>

        {/* Stats Cards */}
        <div className="pm-stats">
          {statCards.map((c) => (
            <div key={c.key} className="pm-stat-card" style={{ "--accent": c.color }}>
              <div className="pm-stat-number" style={{ color: c.color }}>
                {stats[c.key]}
              </div>
              <div className="pm-stat-label">{c.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="pm-tabs">
          {["all", "pendingApproval", "adminApproved", "accepted", "complete", "adminRejected", "influencerRejected"].map((s) => (
            <button
              key={s}
              className={`pm-tab-btn ${statusFilter === s ? "active" : ""}`}
              onClick={() => handleStatusTab(s)}
            >
              {s === "all" ? "All Posts" : formatStatus(s)}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="pm-section">
          {loading ? (
            <div className="pm-loader">Loading posts...</div>
          ) : (
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Vendor</th>
                    <th>Category</th>
                    <th>Promotion</th>
                    <th>City / Pin</th>
                    <th>Description</th>
                    <th>Created At</th>
                    {statusFilter === "all" && <th>Status</th>}
                    {(statusFilter === "all" || statusFilter === "pendingApproval") && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {posts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="pm-empty">
                        No posts found in this category.
                      </td>
                    </tr>
                  ) : (
                    posts.map((item, idx) => (
                      <tr key={item._id}>
                        <td>{(page - 1) * limit + idx + 1}</td>
                        <td>
                          <div className="pm-user-cell">
                            <span className="pm-name">
                              {item.vendorDetails?.name || item.vendorDetails?.shop_name || "N/A"}
                            </span>
                            <span className="pm-email">{item.vendorDetails?.email}</span>
                          </div>
                        </td>
                        <td>{item.categoryDetails?.title || "N/A"}</td>
                        <td>{capitalize(item.promotionType)}</td>
                        <td>{item.city} / {item.pincode}</td>
                        <td className="pm-desc">{item.description}</td>
                        <td>{fmt(item.createdAt)}</td>

                        {statusFilter === "all" && (
                          <td>
                            <span className="pm-category-tag">
                              {formatStatus(item.status)}
                            </span>
                          </td>
                        )}

                        {(statusFilter === "all" || statusFilter === "pendingApproval") && (
                          <td>
                            {item.status === "pendingApproval" ? (
                              <div className="pm-actions">
                                <button className="pm-btn pm-approve-btn" onClick={() => handleApprove(item._id)}>
                                  <FiCheck /> Approve
                                </button>
                                <button className="pm-btn pm-reject-btn" onClick={() => setRejectModal({ show: true, item, reason: "" })}>
                                  <FiX /> Reject
                                </button>
                              </div>
                            ) : (
                              <span style={{ color: '#64748b', fontSize: '13px' }}>No action</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pm-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <FiChevronLeft /> Prev
              </button>
              <span>Page {page} of {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>
                Next <FiChevronRight />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal.show && (
        <div className="pm-modal-overlay" onClick={() => setRejectModal({ show: false, item: null, reason: "" })}>
          <div className="pm-modal-content" onClick={e => e.stopPropagation()}>
            <h3>Reject Post</h3>
            <textarea
              placeholder="Enter rejection reason..."
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              rows={4}
            />
            <div className="pm-modal-actions">
              <button onClick={() => setRejectModal({ show: false, item: null, reason: "" })}>Cancel</button>
              <button onClick={submitReject}>Submit Rejection</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostManagement;